import { Check, Minus } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { RoleModel } from '@/types'

import { PERMISSIONS } from '../../../../permissions'

const POS_MODULES = ['pos', 'pos_reports', 'terminals', 'price_books', 'membership_tiers']

const label = (action: string) => action.replace(/_/g, ' ')

/** The POS permissions of each role in this business side by side, read-only; roles are edited above. */
export const PosPermissionMatrix = ({ roles }: { roles: RoleModel.Role[] }) => {
  const { isFeatureEnabled } = useFeatureFlags()
  const groups = PERMISSIONS.filter((g) => POS_MODULES.includes(g.module)).sort(
    (a, b) => POS_MODULES.indexOf(a.module) - POS_MODULES.indexOf(b.module)
  )
  if (!roles.length || !isFeatureEnabled('pos')) return null
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>POS permissions</CardTitle>
        <CardDescription className="text-text-2">
          What each role on this page can do in the POS. Discount limits are set per staff member.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="border-b border-border-strong text-left text-xs text-text-2">
                <th className="py-2 pr-4 font-medium">Permission</th>
                {roles.map((r) => (
                  <th key={r._id} className="px-3 py-2 text-center font-medium">
                    {r.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows key={g.module} module={g.module} actions={g.actions} roles={roles} />
              ))}
            </tbody>
          </table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  )
}

const GroupRows = ({ module, actions, roles }: { module: string; actions: string[]; roles: RoleModel.Role[] }) => (
  <>
    <tr className="bg-subdued">
      <td colSpan={roles.length + 1} className="px-2 py-1.5 font-mono text-xs font-medium text-ink">
        {module}
      </td>
    </tr>
    {actions.map((action) => {
      const key = `${module}:${action}`
      return (
        <tr key={key} className="border-b border-border-subtle">
          <td className="py-1.5 pl-4 pr-4 text-text-2">
            {label(action)} <span className="font-mono text-xs text-muted-foreground">{key}</span>
          </td>
          {roles.map((r) => (
            <td key={r._id} className="px-3 py-1.5 text-center">
              {r.permissions?.includes(key) || r.permissions?.includes(`${module}:*`) ? (
                <Check className="mx-auto size-4 text-success-dark" aria-label="Allowed" />
              ) : (
                <Minus className="mx-auto size-4 text-faint" aria-label="Not allowed" />
              )}
            </td>
          ))}
        </tr>
      )
    })}
  </>
)
